// Типы для конфигурации сайта
export interface SiteFeature<TConfig = Record<string, any>> {
    enabled: boolean;
    config?: TConfig;
}

export type AuthMethod = 'password' | 'sms';

export interface AuthFeatureConfig {
    enableRegistration: boolean;
    enableSocialLogin: boolean;
    enableGuestCheckout: boolean;
    passwordMinLength: number;
    enabledMethods: AuthMethod[];
    defaultMethod: AuthMethod;
}

export interface SiteFeatures {
    shop: SiteFeature;
    blog: SiteFeature;
    auth: SiteFeature<AuthFeatureConfig>;
    search: SiteFeature;
    cart: SiteFeature;
    wishlist: SiteFeature;
    reviews: SiteFeature;
    notifications: SiteFeature;
    analytics: SiteFeature;
    chatbot: SiteFeature;
    multiCurrency: SiteFeature;
    socialLogin: SiteFeature;
    newsletter: SiteFeature;
    sms: SiteFeature;
    menu: SiteFeature;
}

export interface SiteMetadata {
    title: string;
    description: string;
    keywords?: string;
    author?: string;
    image?: string;
    url?: string;
}

export interface ContactInfo {
    email?: string;
    phone?: string;
    address?: string;
    social?: {
        twitter?: string;
        facebook?: string;
        instagram?: string;
        telegram?: string;
        whatsapp?: string;
    };
}

export interface LanguageConfig {
    code: string;
    name: string;
    nativeName: string;
    path: string;
    icon: string;
    enabled: boolean;
    isDefault?: boolean;
    metadata: SiteMetadata;
    contactInfo: ContactInfo;
    currency?: {
        code: string;
        symbol: string;
        position: 'before' | 'after';
    };
}

export interface SiteConfig {
    defaultLanguage: string;
    languages: LanguageConfig[];
    features: SiteFeatures;
    general: {
        siteName: string;
        logo: string;
        favicon: string;
        timezone: string;
        dateFormat: string;
        showLanguageSwitch: boolean;
    };
    api: {
        baseUrl: string;
        timeout: number;
        retries: number;
    };
    cache: {
        enabled: boolean;
        ttl: number;
    };
    seo: {
        enableSitemap: boolean;
        enableRobots: boolean;
        enableStructuredData: boolean;
    };
    ui?: {
        slider?: {
            heightDesktop?: number;
            heightMobile?: number;
            aspectRatioDesktop?: string; // пример: '1.28 / 1'
            aspectRatioMobile?: string;  // пример: '1.28 / 1'
        };
        productDetail?: {
            showDescription?: boolean;
            showAllSpecifications?: boolean;
            imageFit?: 'contain' | 'cover'; // как заполнять изображение в слайдере на странице товара
            blocks?: {
                description?: boolean;
                allSpecifications?: boolean;
            };
        };
    };
}

// Основная конфигурация сайта
export const siteConfig: SiteConfig = {
    defaultLanguage: 'ru',
    languages: [
        {
            code: 'en',
            name: 'English',
            nativeName: 'English',
            path: '/en',
            icon: '/languages/en.svg',
            enabled: true,
            isDefault: false,
            metadata: {
                title: 'WebGoodPeople - E-commerce & Development',
                description: 'Professional e-commerce solutions and web development services',
                keywords: 'e-commerce, web development, online store',
                author: 'WebGoodPeople',
                url: 'https://webgoodpeople.com'
            },
            contactInfo: {
                email: 'info@webgoodpeople.com',
                phone: '+1-234-567-8900',
                address: '123 Business St, City, Country',
                social: {
                    twitter: 'https://twitter.com/webgoodpeople',
                    instagram: 'https://instagram.com/webgoodpeople',
                    telegram: 'https://t.me/webgoodpeople'
                }
            },
            currency: {
                code: 'USD',
                symbol: '$',
                position: 'before'
            }
        },
        {
            code: 'ru',
            name: 'Русский',
            nativeName: 'Русский',
            path: '',
            icon: '/languages/ru.svg',
            enabled: true,
            isDefault: true,
            metadata: {
                title: 'WebGoodPeople - Электронная коммерция и разработка',
                description: 'Профессиональные решения для электронной коммерции и веб-разработки',
                keywords: 'электронная коммерция, веб-разработка, интернет-магазин',
                author: 'WebGoodPeople',
                url: 'https://webgoodpeople.com/ru'
            },
            contactInfo: {
                email: 'info@webgoodpeople.ru',
                phone: '+7-123-456-7890',
                address: 'ул. Деловая 123, Город, Россия',
                social: {
                    telegram: 'https://t.me/webgoodpeople_ru',
                    instagram: 'https://instagram.com/webgoodpeople_ru'
                }
            },
            currency: {
                code: 'RUB',
                symbol: '₽',
                position: 'after'
            }
        },
        {
            code: 'de',
            name: 'German',
            nativeName: 'Deutsch',
            path: '/de',
            icon: '/languages/de.svg',
            enabled: true,
            metadata: {
                title: 'WebGoodPeople - E-Commerce & Entwicklung',
                description: 'Professionelle E-Commerce-Lösungen und Webentwicklungsdienstleistungen',
                keywords: 'E-Commerce, Webentwicklung, Online-Shop',
                author: 'WebGoodPeople',
                url: 'https://webgoodpeople.com/de'
            },
            contactInfo: {
                email: 'info@webgoodpeople.de',
                phone: '+49-123-456-7890',
                address: 'Geschäftstraße 123, Stadt, Deutschland',
                social: {
                    instagram: 'https://instagram.com/webgoodpeople_de'
                }
            },
            currency: {
                code: 'EUR',
                symbol: '€',
                position: 'after'
            }
        }
    ],
    features: {
        shop: {
            enabled: true,
            config: {
                enableWishlist: true,
                enableCompare: true,
                enableQuickView: true,
                enableVariations: true,
                maxCartItems: 100
            }
        },
        blog: { enabled: false },
        auth: {
            enabled: true,
            config: {
                enableRegistration: true,
                enableSocialLogin: true,
                enableGuestCheckout: true,
                passwordMinLength: 8,
                enabledMethods: ['password'],
                defaultMethod: 'password'
            }
        },
        search: {
            enabled: true,
            config: {
                enableAutoComplete: true,
                enableFilters: true,
                maxResults: 50
            }
        },
        cart: {
            enabled: true,
            config: {
                enablePersistence: true,
                enableCoupons: true,
                enableShipping: true
            }
        },
        wishlist: { enabled: true },
        reviews: {
            enabled: true,
            config: {
                enableRating: true,
                enableImages: true,
                moderationRequired: false
            }
        },
        notifications: {
            enabled: true,
            config: {
                enableEmail: true,
                enablePush: false,
                enableSMS: true
            }
        },
        analytics: {
            enabled: true,
            config: {
                googleAnalytics: process.env.NEXT_PUBLIC_GA_ID,
                yandexMetrica: process.env.NEXT_PUBLIC_YM_ID
            }
        },
        chatbot: { enabled: false },
        multiCurrency: { enabled: true },
        socialLogin: {
            enabled: true,
            config: {
                providers: ['google', 'facebook', 'github']
            }
        },
        newsletter: { enabled: true },
        sms: {
            enabled: true,
            config: {
                provider: 'twilio',
                verificationEnabled: true
            }
        },
        menu: { enabled: true }
    },
    general: {
        siteName: 'WebGoodPeople',
        logo: '/project/logo.svg',
        favicon: '/favicon.ico',
        timezone: 'UTC',
        dateFormat: 'DD/MM/YYYY',
        showLanguageSwitch: true
    },
    api: {
        baseUrl: process.env.CATALOG_API || '',
        timeout: 30000,
        retries: 3
    },
    cache: {
        enabled: true,
        ttl: 3600
    },
    seo: {
        enableSitemap: true,
        enableRobots: true,
        enableStructuredData: true
    },
    ui: {
        slider: {
            aspectRatioDesktop: '40 / 9',
            aspectRatioMobile: '4 / 3',
        },
        productDetail: {
            showDescription: true,
            showAllSpecifications: true,
            imageFit: 'cover',
            blocks: {
                description: true,
                allSpecifications: true,
            },
        }
    }
}; 
